/**
 * Cognito User Management Service
 * Handles admin operations (list / create / delete users) via the backend Lambda.
 *
 * Routes (proxied through /api in dev, full API Gateway URL in prod):
 *   GET    /admin/users           -> list all users
 *   POST   /admin/users           -> create user
 *   DELETE /admin/users/<email>   -> delete user
 *   GET    /admin/users/<email>   -> get single user
 */
import { AuthService } from './auth.service';
import { apiConfig } from '@/config/api.config';
import { API_ENVIRONMENTS } from '@/config/constant';
import type { UserRole } from '@/config/constant';

export interface CognitoUser {
  username: string;
  email: string;
  status: string;
  createdDate: string;
  enabled: boolean;
  groups?: string[];
}

export interface CreateUserInput {
  email: string;
  role: UserRole;
}


class CognitoUserManagementService {
  /**
   * DEV:  /api  -> Webpack proxy strips prefix -> API Gateway
   * PROD: full API Gateway base URL from env var (uses separate admin API)
   */
  private getBaseUrl(): string {
    if (process.env.NODE_ENV === API_ENVIRONMENTS.DEV) {
      return '/api';
    }
    return apiConfig.adminBaseUrl.replace(/\/$/, '');
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const idToken = await AuthService.getIdToken();
    if (!idToken) {
      throw new Error('No authentication token available');
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    };
  }

  /**
   * Safely parses the response body.
   * Returns a clean message if body is HTML (e.g. 404 page) instead of JSON.
   */
  private async parseError(response: Response): Promise<string> {
    const text = await response.text().catch(() => '');
    try {
      const json = JSON.parse(text) as { error?: string; message?: string };
      return json.error || json.message || `HTTP ${response.status}: ${response.statusText}`;
    } catch {
      return `HTTP ${response.status}: ${response.statusText}`;
    }
  }

  async listUsers(): Promise<CognitoUser[]> {
    const headers = await this.getAuthHeaders();
    // Add fetchAll=true query parameter to get all users with pagination handled by Lambda
    const url = `${this.getBaseUrl()}${apiConfig.endpoints.adminUsers}?fetchAll=true`;

    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }

    const result = await response.json() as { users?: CognitoUser[]; data?: CognitoUser[]; success?: boolean };

    // Support both Lambda response shapes:
    //   { users: [...], count: N }   ← actual Lambda
    //   { success: true, data: [...] } ← future-proofed
    const users = result.users ?? result.data;
    if (!users) {
      throw new Error('Failed to retrieve users');
    }
    return users;
  }

  async createUser(input: CreateUserInput): Promise<void> {
    const headers = await this.getAuthHeaders();
    const url = `${this.getBaseUrl()}${apiConfig.endpoints.adminUsers}`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email: input.email, role: input.role }),
    });

    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }

    // Lambda returns { message, email, status } on success (statusCode 200)
    // No need to check result.success field - if response.ok, user was created
    await response.json();
  }

  async deleteUser(email: string): Promise<void> {
    const headers = await this.getAuthHeaders();
    // Delete endpoint matching path /admin/users/{useremail}
    const url = `${this.getBaseUrl()}${apiConfig.endpoints.adminUsers}/${encodeURIComponent(email)}`;

    const response = await fetch(url, { method: 'DELETE', headers });

    if (!response.ok) {
      throw new Error(await this.parseError(response));
    }

    // Lambda returns { message } on success (statusCode 200)
    // No need to check result.success field - if response.ok, user was deleted
    await response.json();
  }

  async getUser(email: string): Promise<CognitoUser | null> {
    try {
      const headers = await this.getAuthHeaders();
      const url = `${this.getBaseUrl()}${apiConfig.endpoints.adminUsers}/${encodeURIComponent(email)}`;
      const response = await fetch(url, { method: 'GET', headers });
      if (!response.ok) return null;

      // Lambda may return the user directly or wrapped in { data: ... }
      const raw = await response.json() as Record<string, unknown>;
      const user = ('data' in raw ? raw['data'] : raw) as CognitoUser | undefined;
      return user ?? null;
    } catch {
      return null;
    }
  }
}

export const cognitoUserManagementService = new CognitoUserManagementService();
export default cognitoUserManagementService;
